import {
  formatForDisplay,
  type HotkeyMeta,
  type HotkeyRegistrationView,
  type RegisterableHotkey,
  type SequenceRegistrationView,
} from "@tanstack/react-hotkeys"

/** Groups listed in this order; any other group (a page's) follows in the order it registered */
const GROUP_ORDER = ["Global", "Navigation", "Views", "Tables"] as const

type ShortcutGroup = (typeof GROUP_ORDER)[number] | (string & {})

interface ShortcutMeta {
  name: string
  group: ShortcutGroup
  /** Shown instead of the keys, for one entry that binds many (the digits) */
  keysLabel?: string
}

/**
 * `meta` for a hotkey or sequence the keyboard shortcuts dialog lists while it's registered and
 * enabled. Bindings without it stay out of the dialog.
 *
 * Cast rather than typed by extending `HotkeyMeta`: augmenting it needs `@tanstack/hotkeys` as a
 * direct dependency, since `@tanstack/react-hotkeys` only re-exports it.
 */
export const shortcutMeta = (group: ShortcutGroup, name: string, keysLabel?: string): HotkeyMeta =>
  ({ name, group, keysLabel }) as ShortcutMeta

/** One way to press an entry: keys held together, or pressed one after the other (G then H) */
interface KeyCombo {
  keys: readonly string[]
  sequence: boolean
}

export interface CheatsheetEntry {
  name: string
  /** Alternatives for the same action, e.g. ⇧J or ⇧↓ */
  combos: KeyCombo[]
  keysLabel?: string
}

export interface CheatsheetGroup {
  title: string
  entries: CheatsheetEntry[]
}

const isShiftedSymbol = (hotkey: Exclude<RegisterableHotkey, string>) =>
  !!hotkey.shift &&
  !hotkey.mod &&
  !hotkey.ctrl &&
  !hotkey.alt &&
  !hotkey.meta &&
  hotkey.key.length === 1 &&
  !/[a-z0-9]/i.test(hotkey.key)

/** A shifted symbol shows as the character typed (?), not as Shift+key */
export const hotkeyDisplay = (hotkey: RegisterableHotkey) =>
  typeof hotkey !== "string" && isShiftedSymbol(hotkey) ? hotkey.key : formatForDisplay(hotkey)

const toMeta = (meta: HotkeyMeta | undefined) => {
  const candidate = meta as Partial<ShortcutMeta> | undefined
  return candidate?.name && candidate.group ? (candidate as ShortcutMeta) : undefined
}

/**
 * The keyboard shortcuts dialog's groups, from the live hotkey and sequence registrations that
 * carry `shortcutMeta` and are enabled. Registrations with the same group and name merge into one
 * entry (a key bound by two mounted components, or several keys for one action).
 */
export function buildShortcutGroups({
  hotkeys,
  sequences,
}: {
  hotkeys: readonly HotkeyRegistrationView[]
  sequences: readonly SequenceRegistrationView[]
}): CheatsheetGroup[] {
  const groups = new Map<string, Map<string, CheatsheetEntry>>()
  const add = (meta: ShortcutMeta, combo: KeyCombo) => {
    const entries = groups.get(meta.group) ?? new Map<string, CheatsheetEntry>()
    groups.set(meta.group, entries)
    const entry = entries.get(meta.name) ?? {
      name: meta.name,
      combos: [],
      keysLabel: meta.keysLabel,
    }
    entries.set(meta.name, entry)
    const id = combo.keys.join(" ")
    if (!entry.combos.some((existing) => existing.keys.join(" ") === id)) entry.combos.push(combo)
  }

  for (const registration of hotkeys) {
    const meta = toMeta(registration.options.meta)
    if (!meta || registration.options.enabled === false) continue
    add(meta, { keys: [hotkeyDisplay(registration.parsedHotkey)], sequence: false })
  }
  for (const registration of sequences) {
    const meta = toMeta(registration.options.meta)
    if (!meta || registration.options.enabled === false) continue
    add(meta, { keys: registration.sequence.map((step) => formatForDisplay(step)), sequence: true })
  }

  const rank = (title: string) => {
    const index = (GROUP_ORDER as readonly string[]).indexOf(title)
    return index === -1 ? GROUP_ORDER.length : index
  }
  return [...groups]
    .map(([title, entries]) => ({ title, entries: [...entries.values()] }))
    .sort((a, b) => rank(a.title) - rank(b.title))
}
